import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTournamentAPI,
  deleteTournamentAPI,
  fetchPaginatedTournamentsAPI,
  fetchTournamentsAPI,
  fetchTournamentsByStatusesAPI,
  fetchTournamentsStatusesAPI,
  updateTournamentAPI,
} from "./tournamentsAPI";
import { showModal } from "../statusModal/statusModal";

const initialState = {
  list: [],
  filteredList: [],
  fetchFilteredStatus: "idle",
  statusesList: [],
  fetchStatus: "idle",
  fetchStatusesStatus: "idle",
  addStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  currentPage: 1,
  totalPages: 1,
  currentFilteredPage: 1,
  totalFilteredPages: 1,
  pageLimit: 10,
  error: null,
};

export const fetchTournaments = createAsyncThunk(
  "tournaments/fetchTournaments",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchTournamentsAPI();
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const fetchTournamentsByStatuses = createAsyncThunk(
  "tournaments/fetchTournamentsByStatuses",
  async ({ page, statuses }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { tournaments } = getState();
      const limit = tournaments.pageLimit;

      const data = await fetchTournamentsByStatusesAPI(page, limit, statuses);

      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const fetchPaginatedTournament = createAsyncThunk(
  "tournaments/fetchPaginatedTournaments",
  async ({ page }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { tournaments } = getState();
      const limit = tournaments.pageLimit;

      const data = await fetchPaginatedTournamentsAPI(page, limit);
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const fetchTournamentsStatuses = createAsyncThunk(
  "tournaments/fetchTournamentsStatuses",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchTournamentsStatusesAPI();

      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const createTournament = createAsyncThunk(
  "tournaments/createTournament",
  async (tournamentData, { dispatch, rejectWithValue }) => {
    try {
      const data = await createTournamentAPI(tournamentData);
      dispatch(
        showModal({
          message: `تم انشاء بطولة جديدة بنجاح`,
          status: "success",
        })
      );
      return data.data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء انشاء البطولة", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete admins"
      );
    }
  }
);

export const deleteTournament = createAsyncThunk(
  "tournaments/deleteTournament",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteTournamentAPI(id);
      dispatch(
        showModal({
          message: `تم حذف ${data.data.name} بنجاح`,
          status: "success",
        })
      );
      return data.data._id;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء حذف البطولة", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete admin"
      );
    }
  }
);

// Fields the backend locks once players hold seats → how the admin reads them.
const LOCKED_FIELD_LABELS = {
  startDate: "تاريخ البداية",
  tournamentType: "نوع البطولة",
  maxUsersByTeam: "عدد لاعبي الفريق",
  gameName: "اللعبة",
};

const lockedFieldsMessage = (fields = []) => {
  const labels = fields.map((field) => LOCKED_FIELD_LABELS[field]).filter(Boolean);
  const list = labels.length ? labels.join("، ") : "هذه البيانات";
  return `لا يمكن تعديل ${list} بعد تسجيل لاعبين في البطولة`;
};

export const updateTournament = createAsyncThunk(
  "tournaments/updateTournament",
  async ({ tournamentId, tournamentData }, { dispatch, rejectWithValue }) => {
    try {
      const data = await updateTournamentAPI(tournamentId, tournamentData);

      dispatch(
        showModal({
          message: `تم التعديل على البطولة بنجاح`,
          status: "success",
        })
      );
      return data.data;
    } catch (error) {
      const errorKey = error.response?.data?.errorKey;
      dispatch(
        showModal({
          message:
            errorKey === "errors.tournament.lockedAfterRegistration"
              ? lockedFieldsMessage(error.response?.data?.params?.fields)
              : "حصل خطأ اثناء التحديث على البطولة",
          status: "error",
        })
      );
      return rejectWithValue(errorKey || "errors.server.internalError");
    }
  }
);

const tournamentsSlice = createSlice({
  name: "tournaments",
  initialState,
  reducers: {
    resetTournamentsList: (state) => {
      state.list = [];
      state.currentPage = 1;
      state.totalPages = 1;
    },
    resetTournamentsFilteredList: (state) => {
      state.filteredList = [];
    },
    handleTournamentWebSocketUpdate: (state, action) => {
      if (action.payload.type === "addTournament") {
        state.list.push(action.payload.tournament);
      }

      // if (action.payload.type)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTournaments.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournaments.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.list = action.payload.data;
      })
      .addCase(fetchTournaments.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(fetchTournamentsByStatuses.pending, (state) => {
        state.fetchFilteredStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournamentsByStatuses.fulfilled, (state, action) => {
        const newData = action.payload.data.data.filter(
          (item) =>
            !state.filteredList.find((existing) => existing._id === item._id)
        );
        state.fetchFilteredStatus = "succeeded";
        state.filteredList = [...state.filteredList, ...newData];
        state.currentFilteredPage = action.payload.data.page;
        state.totalFilteredPages = action.payload.data.totalPages;
      })
      .addCase(fetchTournamentsByStatuses.rejected, (state, action) => {
        state.fetchFilteredStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(fetchPaginatedTournament.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchPaginatedTournament.fulfilled, (state, action) => {
        const page = Number(action.payload?.data?.page || 1);
        const responseList = Array.isArray(action.payload?.data?.data)
          ? action.payload.data.data
          : [];

        const newData =
          page === 1
            ? responseList
            : responseList.filter(
                (item) => !state.list.find((existing) => existing._id === item._id)
              );
        state.fetchStatus = "succeeded";
        state.list = page === 1 ? newData : [...state.list, ...newData];
        state.currentPage = action.payload.data.page;
        state.totalPages = action.payload.data.totalPages;
      })
      .addCase(fetchPaginatedTournament.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(fetchTournamentsStatuses.pending, (state) => {
        state.fetchStatusesStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournamentsStatuses.fulfilled, (state, action) => {
        state.fetchStatusesStatus = "succeeded";
        state.statusesList = action.payload.data;
      })
      .addCase(fetchTournamentsStatuses.rejected, (state, action) => {
        state.fetchStatusesStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(createTournament.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(createTournament.fulfilled, (state, action) => {
        state.addStatus = "succeeded";
        state.list.push(action.payload);
      })
      .addCase(createTournament.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(deleteTournament.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteTournament.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.list = state.list.filter(({ _id }) => _id !== action.payload);
      })
      .addCase(deleteTournament.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(updateTournament.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateTournament.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.list = state.list.map((tournament) => {
          return tournament._id === action.payload._id
            ? action.payload
            : tournament;
        });
      })
      .addCase(updateTournament.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload || action.error?.message;
      });
  },
});

export const { handleTournamentWebSocketUpdate } = tournamentsSlice.actions;

export default tournamentsSlice.reducer;
